import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, HardHat, Trash2, CheckCircle2, ChevronLeft, ChevronRight, Zap, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { EquipmentCard } from '../components/EquipmentCard';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from '../components/ObfuscatedPhone';
import { ObfuscatedLocationEmail } from '../components/ObfuscatedLocationEmail';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generator images (Doosan G70)
const GENERATOR_IMAGES = [
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/vez2lt95_IMG_9061-scaled.jpg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/yr50a3c8_G70_Edit_Decals-Final.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/wnj86eed_bd082c98-d05a-446e-a5ed-0275a8372447.jpg'
];

// Garbage Chutes images (Superchute)
const CHUTE_IMAGES = [
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/p3g7z526_DDE3CCD9-0267-44E0-BC9E-8DEFF78479B8_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/2ej4sev0_178FC5D3-C626-4FCF-800D-BE6215A3BF23_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/2vrokbnr_Hoist-on-deck.png'
];

// Temporary Stairs images (SmartStairs)
const STAIRS_IMAGES = [
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/a7mcsqi7_mp906d.jpg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/84ysii9f_metaltech-smartstairs-17-21-01.png',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/09z89jql_Smartstairs_project_25.jpg'
];

// Dump Trailer images
const TRAILER_IMAGES = [
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/ov0q9oof_IMG_5578.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/30tgtena_IMG_3951.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/1smg9ckp_IMG_3972.jpeg'
];

export default function WorkSite() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language, getLocalizedLink } = useLanguage();

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const response = await axios.get(`${API}/equipment/worksite`);
        setEquipment(response.data);
      } catch (error) {
        console.error('Error fetching equipment:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEquipment();
  }, []);

  const content = {
    en: {
      tagline: 'Kenscho Work-Site',
      title1: 'Work-Site',
      title2: 'Solutions',
      desc: 'Professional equipment and services for construction sites of all sizes. From debris management to safe access solutions, we keep your project moving efficiently.',
      
      // Services
      servicesTitle: 'Our Services',
      servicesSubtitle: 'Everything you need to keep your work site running smoothly',
      
      // Generator (Doosan G70)
      generatorTitle: '2016 DOOSAN G70WJ 83 kVA Mobile Generator',
      generatorCategory: 'Worksite Power',
      generatorDesc: 'Reliable mobile power for your worksite or special event. Powered by a 4-cylinder diesel engine (John Deere) capable of 83 kVA (66 kW) standby / ~70 kVA prime power output.',
      generatorFeatures: [
        'Tier 4 Final (T4F) compliant engine with Diesel Oxidation Catalyst (DOC)',
        'Virtually maintenance-free — no diesel particulate filter (DPF) required',
        'Ultra-quiet: 64-65 dB(A) at 100% load — ideal for special events',
        '3-Phase output: 480V / 208V / 240V switchable',
        '83 kVA standby / ~70 kVA prime power',
        'Towable and easy to position on site'
      ],
      
      // Garbage Chutes (Superchute brand)
      chutesTitle: 'Garbage Chutes',
      chutesBrand: 'Powered by Superchute',
      chutesDesc: 'Rent a fully customized garbage chute system for your worksite. Our Superchute systems can be configured to any building height with special requirements tailored to your project.',
      chutesFeatures: [
        'Installed with or without anchoring to the existing structure',
        'Self-erected without a crane — can be carried up stairs',
        'Mounts from the roof or any window on the building side',
        'Fully customizable entry points on each floor, including roof',
        'Configured to any building height',
        'Quick installation and removal'
      ],
      
      // Temporary Stairs
      stairsTitle: 'Temporary Stairs',
      stairsDesc: 'Portable aluminum stair systems for safe access during construction. Quick to install and remove — just 30 minutes per staircase with only 2 people. No heavy machinery or bulky scaffolds required.',
      stairsFeatures: [
        'Small: 22.5" to 37.5" (1.9 ft – 3.1 ft)',
        'Medium: 45" to 75" (3.75 ft – 6.25 ft)',
        'Large: 82.5" to 120" (6.8 ft – 10 ft)',
        'Extra Large: 127.5" to 157.5" (10.6 ft – 13.1 ft)',
        'Install with or without landing platform',
        'Supports 500 lbs per step'
      ],
      
      // Dump Trailer
      trailerTitle: 'Mobile Dump Trailer',
      trailerDesc: 'Our mobile dump trailer can be easily moved around your construction site as your project evolves. Perfect when you don\'t need a full 40-yard bin — holds approximately 18 cubic yards and up to 4 tons of waste.',
      trailerFeatures: [
        'Easily repositioned around the job site',
        'Ideal for separating waste: bricks & masonry, wood, or non-recyclables',
        'Use as a general waste bin for smaller projects',
        'Emptied daily, weekly, monthly, or at project end',
        'Holds ~18 cubic yards / up to ~4 tons',
        'Perfect for customized waste management plans'
      ],
      
      // Equipment section
      equipmentTitle: 'Available Equipment',
      equipmentSubtitle: 'Browse our work-site equipment inventory',
      requestEquipment: 'Request Equipment',
      
      // Why choose
      whyChoose: 'Why Choose Kenscho Work-Site',
      whyDesc: 'We understand the demands of active construction sites.',
      features: [
        { title: 'Fast Delivery', desc: 'Quick delivery and setup to minimize your downtime.' },
        { title: 'Flexible Rentals', desc: 'Daily, weekly, or project-length rental terms available.' },
        { title: 'Expert Support', desc: 'Our team helps you choose the right equipment for your needs.' }
      ],
      
      getQuote: 'Get a Quote',
      learnMore: 'Learn More',
      worksiteCta: 'Ready to get started?',
      worksiteCtaDesc: 'Call now to schedule a delivery or get a quote for your worksite equipment needs.',
      worksiteCtaButton: 'Get a Quote'
    },
    fr: {
      tagline: 'Kenscho Chantier',
      title1: 'Solutions de',
      title2: 'Chantier',
      desc: 'Équipement et services professionnels pour chantiers de construction de toutes tailles. De la gestion des débris aux solutions d\'accès sécuritaires, nous gardons votre projet en mouvement.',
      
      // Services
      servicesTitle: 'Nos Services',
      servicesSubtitle: 'Tout ce dont vous avez besoin pour garder votre chantier en bon fonctionnement',
      
      // Generator (Doosan G70)
      generatorTitle: '2016 DOOSAN G70WJ 83 kVA Génératrice Mobile',
      generatorCategory: 'Alimentation de chantier',
      generatorDesc: 'Alimentation mobile fiable pour votre chantier ou événement spécial. Propulsé par un moteur diesel 4 cylindres (John Deere) capable de 83 kVA (66 kW) en veille / ~70 kVA en puissance continue.',
      generatorFeatures: [
        'Moteur conforme Tier 4 Final (T4F) avec catalyseur d\'oxydation diesel (DOC)',
        'Pratiquement sans entretien — aucun filtre à particules diesel (DPF) requis',
        'Ultra-silencieux: 64-65 dB(A) à 100% de charge — idéal pour événements spéciaux',
        'Sortie triphasée: 480V / 208V / 240V commutable',
        '83 kVA en veille / ~70 kVA en puissance continue',
        'Remorquable et facile à positionner sur le site'
      ],
      
      // Garbage Chutes (Superchute brand)
      chutesTitle: 'Goulottes à débris',
      chutesBrand: 'Propulsé par Superchute',
      chutesDesc: 'Louez un système de goulotte à débris entièrement personnalisé pour votre chantier. Nos systèmes Superchute peuvent être configurés à n\'importe quelle hauteur de bâtiment avec des exigences spéciales adaptées à votre projet.',
      chutesFeatures: [
        'Installation avec ou sans ancrage à la structure existante',
        'Auto-érigé sans grue — peut être transporté dans les escaliers',
        'Se monte depuis le toit ou n\'importe quelle fenêtre sur le côté du bâtiment',
        'Points d\'entrée entièrement personnalisables à chaque étage, y compris le toit',
        'Configuré à n\'importe quelle hauteur de bâtiment',
        'Installation et retrait rapides'
      ],
      
      // Temporary Stairs
      stairsTitle: 'Escaliers temporaires',
      stairsDesc: 'Systèmes d\'escaliers en aluminium portables pour un accès sécuritaire pendant la construction. Installation et retrait rapides — seulement 30 minutes par escalier avec 2 personnes. Aucune machinerie lourde ni échafaudage encombrant requis.',
      stairsFeatures: [
        'Petit: 22.5" à 37.5" (1.9 pi – 3.1 pi)',
        'Moyen: 45" à 75" (3.75 pi – 6.25 pi)',
        'Grand: 82.5" à 120" (6.8 pi – 10 pi)',
        'Très grand: 127.5" à 157.5" (10.6 pi – 13.1 pi)',
        'Installation avec ou sans plateforme de palier',
        'Supporte 500 lb par marche'
      ],
      
      // Dump Trailer
      trailerTitle: 'Remorque-benne mobile',
      trailerDesc: 'Notre remorque-benne mobile peut être facilement déplacée sur votre chantier au fur et à mesure de l\'évolution du projet. Parfaite quand vous n\'avez pas besoin d\'un conteneur de 40 verges — contient environ 18 verges cubes et jusqu\'à 4 tonnes de déchets.',
      trailerFeatures: [
        'Facilement repositionnable sur le chantier',
        'Idéale pour séparer les déchets: briques et maçonnerie, bois, ou non-recyclables',
        'Utilisez comme poubelle générale pour les petits projets',
        'Vidée quotidiennement, hebdomadairement, mensuellement, ou à la fin du projet',
        'Contient ~18 verges cubes / jusqu\'à ~4 tonnes',
        'Parfaite pour des plans de gestion des déchets personnalisés'
      ],
      
      // Equipment section
      equipmentTitle: 'Équipement disponible',
      equipmentSubtitle: 'Parcourez notre inventaire d\'équipement de chantier',
      requestEquipment: 'Demander de l\'équipement',
      
      // Why choose
      whyChoose: 'Pourquoi choisir Kenscho Chantier',
      whyDesc: 'Nous comprenons les exigences des chantiers de construction actifs.',
      features: [
        { title: 'Livraison rapide', desc: 'Livraison et installation rapides pour minimiser vos temps d\'arrêt.' },
        { title: 'Locations flexibles', desc: 'Termes de location quotidiens, hebdomadaires ou pour la durée du projet.' },
        { title: 'Support expert', desc: 'Notre équipe vous aide à choisir le bon équipement pour vos besoins.' }
      ],
      
      getQuote: 'Obtenir un devis',
      learnMore: 'En savoir plus',
      worksiteCta: 'Prêt à commencer?',
      worksiteCtaDesc: 'Appelez maintenant pour planifier une livraison ou obtenir un devis pour vos besoins en équipement de chantier.',
      worksiteCtaButton: 'Obtenir un devis'
    }
  };

  const t = content[language];

  // SEO Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Work-Site Equipment Rental",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Kenscho Design",
      "telephone": "+1-514-291-3737",
      "email": "Location@Kenscho.ca",
      "address": { "@type": "PostalAddress", "addressRegion": "QC", "addressCountry": "CA" },
      "areaServed": ["Lanaudière", "Laurentians", "Laval", "Montreal"]
    },
    "name": language === 'fr' ? "Équipement de Chantier" : "Work-Site Equipment",
    "description": language === 'fr' 
      ? "Location d'équipement de chantier: génératrices, goulottes à débris, escaliers temporaires, remorques-bennes."
      : "Work-site equipment rental: generators, garbage chutes, temporary stairs, dump trailers."
  };

  // Image Gallery component for service cards
  const ImageGallery = ({ images, title }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    
    if (!images || images.length === 0) return null;
    
    const nextImage = (e) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev + 1) % images.length);
    };
    
    const prevImage = (e) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };
    
    return (
      <div className="relative aspect-[4/3] overflow-hidden bg-muted group rounded-lg">
        <img 
          src={images[currentIndex]} 
          alt={`${title} - ${currentIndex + 1}`}
          className="w-full h-full object-cover transition-transform duration-500"
        />
        {images.length > 1 && (
          <>
            <button 
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button 
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, idx) => (
                <button 
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-3' : 'bg-white/50'}`}
                />
              ))}
            </div>
            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
              {currentIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>
    );
  };

  const ServiceCard = ({ title, brand, desc, features, icon, images, imagePosition = 'right' }) => (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden p-8">
      {/* Headline above both */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
          {icon}
        </div>
        <div>
          <h3 className="text-2xl font-light">{title}</h3>
          {brand && (
            <p className="text-sm text-secondary font-medium">{brand}</p>
          )}
        </div>
      </div>
      
      {/* Content grid: text and image side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Text content */}
        <div className={imagePosition === 'left' ? 'order-last' : 'order-first'}>
          <p className="text-muted-foreground mb-6 whitespace-pre-line">{desc}</p>
          <ul className="space-y-3">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
          <Button asChild className="rounded-full mt-6">
            <Link to={getLocalizedLink('/contact')}>
              {t.getQuote} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
        
        {/* Image Gallery */}
        {images && images.length > 0 && (
          <div className={imagePosition === 'left' ? 'order-first' : 'order-last'}>
            <ImageGallery images={images} title={title} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="grain-texture pt-20" data-testid="work-site-page">
      <Helmet>
        <title>{language === 'fr' 
          ? 'Équipement de Chantier | Génératrices, Goulottes, Escaliers | Lanaudière, Laurentides, Laval, Montréal | Kenscho'
          : 'Work-Site Equipment | Generators, Chutes, Stairs | Lanaudière, Laurentians, Laval, Montreal | Kenscho'}
        </title>
        <meta name="description" content={language === 'fr'
          ? 'Location d\'équipement de chantier à Lanaudière, Laurentides, Laval et Montréal. Génératrices mobiles, goulottes à débris, escaliers temporaires, remorques-bennes. RBQ 5829 4596 01.'
          : 'Work-site equipment rental in Lanaudière, Laurentians, Laval, and Montreal. Mobile generators, garbage chutes, temporary stairs, dump trailers. RBQ 5829 4596 01.'} />
        <meta name="keywords" content={language === 'fr'
          ? 'équipement chantier, génératrice, goulotte débris, escalier temporaire, remorque benne, location équipement, Lanaudière, Laurentides, Laval, Montréal'
          : 'work site equipment, generator, garbage chute, temporary stairs, dump trailer, equipment rental, Lanaudière, Laurentians, Laval, Montreal'} />
        <link rel="canonical" href={`https://www.kenscho.ca${getLocalizedLink('/work-site')}`} />
        
        <link rel="alternate" hreflang="en" href="https://www.kenscho.ca/work-site" />
        <link rel="alternate" hreflang="fr" href="https://www.kenscho.ca/fr/work-site" />
        <link rel="alternate" hreflang="x-default" href="https://www.kenscho.ca/work-site" />
        
        <meta property="og:title" content={language === 'fr' ? 'Équipement de Chantier | Kenscho Design' : 'Work-Site Equipment | Kenscho Design'} />
        <meta property="og:description" content={language === 'fr'
          ? 'Location d\'équipement de chantier. Génératrices, goulottes, escaliers, remorques.'
          : 'Work-site equipment rental. Generators, chutes, stairs, dump trailers.'} />
        <meta property="og:url" content={`https://www.kenscho.ca${getLocalizedLink('/work-site')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />
        
        <meta name="geo.region" content="CA-QC" />
        <meta name="geo.placename" content="Lanaudière, Laurentians, Laval, Montreal" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
              <HardHat className="h-6 w-6 text-white" />
            </div>
            <span className="text-sm font-medium tracking-widest uppercase text-secondary animate-fade-in-up">
              {t.tagline}
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6 animate-fade-in-up animation-delay-100">
            {t.title1} <span className="text-secondary">{t.title2}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl animate-fade-in-up animation-delay-200">
            {t.desc}
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h3 className="text-2xl font-light mb-2">{t.worksiteCta}</h3>
                <p className="text-muted-foreground">{t.worksiteCtaDesc}</p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <Phone className="h-5 w-5 text-secondary flex-shrink-0" />
                    <ObfuscatedPhone className="font-medium whitespace-nowrap" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-secondary flex-shrink-0" />
                    <ObfuscatedLocationEmail className="font-medium" />
                  </div>
                </div>
                <Button asChild className="rounded-full btn-animate">
                  <Link to={getLocalizedLink('/contact')}>
                    {t.worksiteCtaButton} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-light tracking-tight mb-4">{t.servicesTitle}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t.servicesSubtitle}</p>
          </div>

          <div className="space-y-8">
            {/* Generator (Doosan G70) - First item: image right */}
            <ServiceCard
              title={t.generatorTitle}
              brand={t.generatorCategory}
              desc={t.generatorDesc}
              features={t.generatorFeatures}
              icon={<Zap className="h-6 w-6 text-secondary" />}
              images={GENERATOR_IMAGES}
              imagePosition="right"
            />

            {/* Garbage Chutes (Superchute) - Second item: image left */}
            <ServiceCard
              title={t.chutesTitle}
              brand={t.chutesBrand}
              desc={t.chutesDesc}
              features={t.chutesFeatures}
              icon={<Trash2 className="h-6 w-6 text-secondary" />}
              images={CHUTE_IMAGES}
              imagePosition="left"
            />

            {/* Temporary Stairs - Third item: image right */}
            <ServiceCard
              title={t.stairsTitle}
              desc={t.stairsDesc}
              features={t.stairsFeatures}
              icon={<HardHat className="h-6 w-6 text-secondary" />}
              images={STAIRS_IMAGES}
              imagePosition="right"
            />

            {/* Mobile Dump Trailer - Fourth item: image left */}
            <ServiceCard
              title={t.trailerTitle}
              desc={t.trailerDesc}
              features={t.trailerFeatures}
              icon={<Trash2 className="h-6 w-6 text-secondary" />}
              images={TRAILER_IMAGES}
              imagePosition="left"
            />
          </div>
        </div>
      </section>

      {/* Equipment Grid */}
      {equipment.length > 0 && (
        <section className="py-24 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <h2 className="text-3xl font-light tracking-tight">
                  {t.equipmentTitle}
                </h2>
                <p className="text-muted-foreground mt-2">
                  {t.equipmentSubtitle}
                </p>
              </div>
              <Button asChild variant="outline" className="rounded-full">
                <Link to={getLocalizedLink('/contact')}>
                  {t.requestEquipment} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-muted animate-pulse h-96 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {equipment.map((item) => (
                  <div key={item.id} className="stagger-item">
                    <EquipmentCard equipment={item} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Why Choose */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-light tracking-tight mb-4">
              {t.whyChoose}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t.whyDesc}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.features.map((feature, index) => (
              <div key={index} className="bg-card border border-border/60 rounded-lg p-8 text-center">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-light text-secondary">{index + 1}</span>
                </div>
                <h3 className="text-lg font-medium mb-3">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="rounded-full btn-animate" data-testid="worksite-cta-btn">
              <Link to={getLocalizedLink('/contact')}>
                {t.getQuote} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
